"use client";

/**
 * Keeps open tabs on the newest build. Moving between pages in an open tab never downloads the app again,
 * so after a deploy a tab could keep showing the old design (and, mixed with new styles, broken layouts)
 * until a hard reload. This asks the server which build is live (/build-id) when the tab comes back into
 * view, every 10 minutes, and at most once a minute while you move around. When it's newer:
 *   - the next page you open loads fully, so it arrives in the new version without anything being lost;
 *   - a small note offers to refresh now.
 */
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { RefreshCw, X } from "lucide-react";

const MINE = process.env.NEXT_PUBLIC_BUILD_ID ?? "";

export function BuildWatcher() {
  const pathname = usePathname();
  const [stale, setStale] = useState(false);
  const [hidden, setHidden] = useState(false);
  const last = useRef(0);
  const first = useRef(true);
  const staleRef = useRef(false);

  useEffect(() => {
    if (!MINE || process.env.NODE_ENV !== "production") return;
    const check = () => {
      if (staleRef.current || Date.now() - last.current < 60_000) return;
      last.current = Date.now();
      fetch(`/build-id?t=${Date.now()}`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { id?: string } | null) => {
          if (d?.id && d.id !== MINE) {
            // At most one reload per server build: if this tab already reloaded for it and still differs
            // (a cache in between), leave it alone instead of reloading on every page.
            let already = false;
            try {
              already = window.sessionStorage.getItem("rb_build_reloaded") === d.id;
              if (!already) window.sessionStorage.setItem("rb_build_reloaded", d.id);
            } catch {
              /* storage blocked: the note below still offers Refresh */
            }
            if (already) return;
            staleRef.current = true;
            setStale(true);
          }
        })
        .catch(() => {});
    };
    const onVisible = () => document.visibilityState === "visible" && check();
    check();
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(check, 10 * 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);

  // A newer build is live and you moved to another page: load that page fully, in the new version.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (staleRef.current) window.location.reload();
    else last.current = Math.min(last.current, Date.now() - 60_000 + 5_000); // look again shortly after moving
  }, [pathname]);

  if (!stale || hidden) return null;
  return (
    <div role="status" data-rb-build-note className="fixed bottom-5 left-1/2 z-[1250] flex -translate-x-1/2 items-center gap-3 rounded-2xl bg-[#140f33] py-2.5 pl-4 pr-2 text-[13px] text-white shadow-2xl ring-1 ring-white/10">
      <span>A new version of Rankbist is ready.</span>
      <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[12.5px] font-bold text-[#140f33] hover:bg-white/90">
        <RefreshCw size={13} /> Refresh
      </button>
      <button type="button" onClick={() => setHidden(true)} className="rounded-lg p-1.5 text-white/60 hover:text-white" aria-label="Later">
        <X size={15} />
      </button>
    </div>
  );
}
